import React from "react";
import { Overrides } from "baseui/overrides";
import { Checkbox as BaseCheckbox, CheckboxProps as BaseCheckboxProps } from "baseui/checkbox";
import { mergeOverrides } from "baseui";
import { Theme } from "../../../../styling/baseui_theme";

export * from "baseui/checkbox";

export interface CheckboxProps extends BaseCheckboxProps {
  color?: string;
  ariaLabel?: string;
  dataTest?: string;
  dataAnalyticsId?: string;
}

const getOverrides = (
  dataTest: string,
  dataAnalyticsId: string | undefined,
): Overrides<
  BaseCheckbox & {
    $disabled: boolean;
    $checked: boolean;
    $isIndeterminate: boolean;
    $error: boolean;
  }
> => {
  return {
    Root: {
      props: {
        "data-analytics-id": dataAnalyticsId,
      },
      style: ({ $theme }) => ({
        alignItems: "center",
        marginTop: "0px",
        marginBottom: $theme.sizing.scale300,
        paddingTop: $theme.sizing.scale0,
        paddingBottom: $theme.sizing.scale0,
      }),
    },
    Label: {
      props: {
        "data-test": `${dataTest}-label`,
      },
      style: ({ $theme }) => {
        return {
          fontWeight: $theme.typography.ParagraphMedium.fontWeight,
        };
      },
    },
    ToggleTrack: {
      props: {
        "data-test": `${dataTest}-toggle`,
      },
      style: ({ $theme, $checked, $disabled }) => ({
        borderTopLeftRadius: $theme.sizing.scale800,
        borderTopRightRadius: $theme.sizing.scale800,
        borderBottomLeftRadius: $theme.sizing.scale800,
        borderBottomRightRadius: $theme.sizing.scale800,
        height: $theme.sizing.scale800,
        paddingLeft: "2px",
        paddingRight: "2px",
        backgroundColor: $disabled
          ? $theme.colors.mono200
          : $checked
            ? $theme.colors.backgroundAccent
            : $theme.colors.mono400,
      }),
    },
    Checkmark: {
      props: {
        "data-test": `${dataTest}-checkbox`,
      },
      style: ({ $theme, $disabled, $checked, $isIndeterminate, $error, $isFocused }) => {
        const color = $disabled
          ? $theme.colors.mono400
          : $checked || $isIndeterminate
            ? $theme.colors.backgroundAccent
            : $error
              ? $theme.colors.borderNegative
              : $theme.colors.mono600;
        return {
          borderTopColor: color,
          borderRightColor: color,
          borderBottomColor: color,
          borderLeftColor: color,
          ...($isFocused
            ? {
                outline: "none",
                ":before": {
                  width: `${$theme.sizing.scale800}`,
                  height: `${$theme.sizing.scale800}`,
                  left: "-7px",
                  top: "-7px",
                  display: "inline-block",
                  position: "relative",
                  content: "''",
                  border: `2px solid ${$theme.colors.borderAccent}`,
                },
              }
            : {}),
        };
      },
    },
    Toggle: {
      style: ({ $theme, $disabled }) => ({
        backgroundColor: $disabled ? $theme.colors.mono400 : $theme.colors.backgroundAlwaysLight,
      }),
    },
  };
};

const getColorOverrides = (color: string) => {
  const colorOverride = {
    Root: {
      style: () => ({
        borderTopColor: color,
        borderRightColor: color,
        borderBottomColor: color,
        borderLeftColor: color,
      }),
    },
    Checkmark: {
      style: ({ $theme, $checked }: { $theme: Theme; $checked: boolean }) => ({
        borderTopColor: color,
        borderRightColor: color,
        borderBottomColor: color,
        borderLeftColor: color,
        backgroundColor: $checked ? color : $theme.colors.white,
      }),
    },
  };

  return colorOverride;
};

export const Checkbox = ({
  overrides,
  color,
  ariaLabel,
  dataTest = "checkbox",
  dataAnalyticsId,
  ...props
}: CheckboxProps) => {
  let mergedOverrides = mergeOverrides(
    getOverrides(dataTest, dataAnalyticsId),
    overrides as Overrides<
      BaseCheckbox & { $checked: boolean; $disabled: boolean; $isIndeterminate: boolean }
    >,
  );

  if (color) {
    mergedOverrides = mergeOverrides(mergedOverrides, getColorOverrides(color));
  }

  return <BaseCheckbox {...props} overrides={mergedOverrides} ariaLabel={ariaLabel} />;
};

export default Checkbox;
